import { matchPath } from "react-router-dom";
import {
  ensureMinSeoTitle,
  fitSeoTitle,
  truncateMetaDescription,
} from "./seoHelpers";

export const SITE_URL = "https://tintlaunch.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/** Social / Open Graph copy (not shown on the page). */
export const DEFAULT_OG_TITLE =
  "TINT — Startup Projects, Jobs, Equity & NDA Management";
export const DEFAULT_OG_DESCRIPTION =
  "Manage your startup projects, jobs, and equity in one place. Includes automated NDA signing and email notifications.";
export const DEFAULT_TWITTER_DESCRIPTION =
  "Manage projects, equity, and NDAs for your startup in one place.";

/** Default SERP meta description (≤155 chars). */
export const DEFAULT_META_DESCRIPTION = truncateMetaDescription(
  "Manage startup projects, jobs, and equity on TINT. Includes automated NDA signing, email alerts, and curated founder resources."
);

/** Visible browser-tab labels kept short where they were before. */
export const HOME_TITLE = "TINT";
export const DEFAULT_TITLE = "TINT";

const TITLE_MAX_LENGTH = 60;

function formatLabel(value) {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, " ");
}

/** `<title>` tag value — min 30 chars for SERP, max ~60. */
export function buildSeoTitleFromLabel(label, suffix = " | TINT") {
  const candidate = `${label}${suffix}`;
  return ensureMinSeoTitle(fitSeoTitle(candidate));
}

/** Visible tab title for /resource/:code pages. */
export function buildResourceVisibleTitle(resourceName) {
  return resourceName ? `${resourceName} | TINT` : "TINT Resource";
}

/** SERP `<title>` for /resource/:code pages. */
export function buildResourceSeoTitle(resourceName) {
  if (!resourceName) {
    return buildSeoTitleFromLabel("Startup Tools & Resources Directory");
  }
  const suffix = " — Startup Tool & Resource | TINT";
  const maxNameLength = TITLE_MAX_LENGTH - suffix.length;
  const name =
    resourceName.length > maxNameLength
      ? `${resourceName.slice(0, maxNameLength - 1).trim()}…`
      : resourceName;
  return ensureMinSeoTitle(fitSeoTitle(`${name}${suffix}`));
}

/** Social share title for /resource/:code pages. */
export function buildResourceOgTitle(resourceName) {
  return buildResourceSeoTitle(resourceName);
}

/** Social share title for /category/:code pages. */
export function buildCategoryOgTitle(categoryName, categoryCode) {
  const label = categoryName || formatLabel(categoryCode);
  const suffix = " Startup Tools & Resources | TINT";
  const maxNameLength = TITLE_MAX_LENGTH - suffix.length;
  const name =
    label.length > maxNameLength
      ? `${label.slice(0, maxNameLength - 1).trim()}…`
      : label;
  return fitSeoTitle(`${name}${suffix}`);
}

/** SERP meta description for resource detail pages. */
export function buildResourceMetaDescription(resource) {
  const fallback =
    "Explore this startup resource on TINT — trusted tools and connections curated for founders and teams.";
  const raw = resource?.description?.trim() || fallback;
  return truncateMetaDescription(raw);
}

/** Rich snippet directives for public indexable pages. */
export const PUBLIC_ROBOTS =
  "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";

/** Auth, dashboard, and utility pages that should not appear in search results. */
export const PRIVATE_ROBOTS = "noindex, nofollow";

export const INDEXABLE_ROUTE_PATTERNS = [
  "/",
  "/categories",
  "/category/:catCode",
  "/resource/:resCode",
  "/contact",
  "/privacy-policy",
  "/terms-and-conditions",
];

export function getRobotsContentForPath(pathname) {
  const isIndexable = INDEXABLE_ROUTE_PATTERNS.some((pattern) =>
    matchPath({ path: pattern, end: true }, pathname)
  );
  return isIndexable ? PUBLIC_ROBOTS : PRIVATE_ROBOTS;
}

/**
 * Route SEO config.
 * `title` = legacy short label. `seoTitle` = `<title>` tag (≥30 chars).
 * `metaDescription` = `<meta name="description">` (≤155 chars).
 */
export const ROUTE_SEO = [
  {
    path: "/",
    title: HOME_TITLE,
    seoTitle: HOME_TITLE,
    ogTitle: DEFAULT_OG_TITLE,
    metaDescription: truncateMetaDescription(
      "Join TINT to manage startup projects, jobs, and equity. Access curated resources, automated NDAs, and founder tools in one place."
    ),
    description: DEFAULT_OG_DESCRIPTION,
    twitterDescription: DEFAULT_TWITTER_DESCRIPTION,
  },
  {
    path: "/profile/:userId",
    title: "Dashboard | TINT",
    seoTitle: "Startup Dashboard & Projects | TINT",
    metaDescription: DEFAULT_META_DESCRIPTION,
    description: DEFAULT_OG_DESCRIPTION,
  },
  {
    path: "/userprofile",
    title: "Profile | TINT",
    seoTitle: "Your TINT Profile & Account Settings",
    metaDescription: truncateMetaDescription(
      "Manage your TINT profile, startup connections, saved resources, and account preferences in one place."
    ),
  },
  {
    path: "/projects",
    title: "Ideas | TINT",
    seoTitle: "Startup Ideas & Project Board | TINT",
    metaDescription: truncateMetaDescription(
      "Discover and manage startup ideas and projects on the TINT platform with your team."
    ),
  },
  {
    path: "/organizations",
    title: "Organizations | TINT",
    seoTitle: "Startup Organizations on TINT Platform",
    metaDescription: truncateMetaDescription(
      "Browse and connect with startup organizations, teams, and partners on TINT."
    ),
  },
  {
    path: "/jobsearch",
    title: "Job Search | TINT",
    seoTitle: "Startup Job Search & Opportunities | TINT",
    metaDescription: truncateMetaDescription(
      "Find startup jobs and opportunities tailored for founders, operators, and early-stage teams."
    ),
  },
  {
    path: "/categories",
    title: "Resources | TINT",
    seoTitle: "Startup Resources & Tools Directory | TINT",
    ogTitle: "Startup Tools & Resources Directory — Browse | TINT",
    metaDescription: truncateMetaDescription(
      "Browse curated startup tools, resources, and categories reviewed by the TINT founder community."
    ),
    description: "Browse curated startup tools, resources, and categories on TINT.",
    twitterDescription: "Browse curated startup tools and resources on TINT.",
  },
  {
    path: "/myresources",
    title: "My Resources | TINT",
    seoTitle: "My Saved Startup Resources | TINT",
    metaDescription: truncateMetaDescription(
      "View and manage your saved startup resources, recommendations, and submissions on TINT."
    ),
  },
  {
    path: "/settings",
    title: "Settings | TINT",
    seoTitle: "Account Settings & Preferences | TINT",
    metaDescription: truncateMetaDescription(
      "Update your TINT account settings, notifications, security, and platform preferences."
    ),
  },
  {
    path: "/projectdetails/:projId",
    title: "Project Details | TINT",
    seoTitle: "Startup Project Details & Equity | TINT",
    metaDescription: truncateMetaDescription(
      "View startup project details, equity breakdowns, milestones, and team information on TINT."
    ),
  },
  {
    path: "/jobdetails/:jobId",
    title: "Job Details | TINT",
    seoTitle: "Startup Job Opportunity Details | TINT",
    metaDescription: truncateMetaDescription(
      "Explore startup job opportunity details, requirements, and application info on TINT."
    ),
  },
  {
    path: "/organizationdetails/:orgId",
    title: "Organization Details | TINT",
    seoTitle: "Startup Organization Profile | TINT",
    metaDescription: truncateMetaDescription(
      "View organization details, startup connections, and team profiles on TINT."
    ),
  },
  {
    path: "/category/:catCode",
    title: "Resources | TINT",
    seoTitle: "Startup Resource Categories & Tools | TINT",
    ogTitle: "Startup Resource Categories — Tools & Guides | TINT",
    metaDescription: truncateMetaDescription(
      "Browse startup tools and resources in this category, curated and reviewed on TINT."
    ),
    description: "Browse startup tools and resources in this category on TINT.",
    twitterDescription: "Browse startup tools and resources in this category on TINT.",
  },
  {
    path: "/jobapplicantlist/:jobId",
    title: "Job Applicants | TINT",
    seoTitle: "Review Job Applicants | TINT Platform",
    metaDescription: truncateMetaDescription(
      "Review and manage applicants for your startup job posting on TINT."
    ),
  },
  {
    path: "/onboarding/project/:projectId",
    title: "Onboarding | TINT",
    seoTitle: "Startup Team Onboarding Wizard | TINT",
    metaDescription: truncateMetaDescription(
      "Complete your startup project onboarding steps on the TINT platform."
    ),
  },
  {
    path: "/onboarding/:onboardingId",
    title: "Onboarding | TINT",
    seoTitle: "Startup Team Onboarding Wizard | TINT",
    metaDescription: truncateMetaDescription(
      "Complete your startup onboarding workflow on the TINT platform."
    ),
  },
  {
    path: "/onboarding",
    title: "Onboarding | TINT",
    seoTitle: "Startup Team Onboarding Wizard | TINT",
    metaDescription: truncateMetaDescription(
      "Complete your startup onboarding on TINT and get your team set up quickly."
    ),
  },
  {
    path: "/resource/:resCode",
    title: "Resources | TINT",
    seoTitle: "Startup Tools & Resources Directory | TINT",
    ogTitle: "Startup Tools & Resources — Curated Directory | TINT",
    metaDescription: truncateMetaDescription(
      "Explore startup tools and resources curated and reviewed by the TINT community."
    ),
    description: "Explore startup tools and resources curated on TINT.",
    twitterDescription: "Explore startup tools and resources curated on TINT.",
  },
  {
    path: "/signin",
    title: "Sign In | TINT",
    seoTitle: "Sign In to Your TINT Startup Account",
    metaDescription: truncateMetaDescription(
      "Sign in to manage your startup projects, jobs, equity, and resources on TINT."
    ),
  },
  {
    path: "/signup",
    title: "Sign Up | TINT",
    seoTitle: "Create Your Free TINT Startup Account",
    metaDescription: truncateMetaDescription(
      "Create a free TINT account to manage projects, equity, NDAs, and startup resources."
    ),
  },
  {
    path: "/resetpassword",
    title: "Reset Password | TINT",
    seoTitle: "Reset Your TINT Account Password",
    metaDescription: truncateMetaDescription(
      "Reset your TINT account password securely and regain access to your startup workspace."
    ),
  },
  {
    path: "/confirm",
    title: "Confirm Your Account | TINT",
    seoTitle: "Confirm Your TINT Account Registration",
    metaDescription: truncateMetaDescription(
      "Confirm your TINT account email to start managing projects, jobs, and equity."
    ),
  },
  {
    path: "/contact",
    title: "Contact TINT | Get Support & Connect With Our Team",
    seoTitle: "Contact TINT | Get Support & Connect With Our Team",
    ogTitle: "Contact TINT — Startup Support, Help & Platform Questions",
    metaDescription: truncateMetaDescription(
      "Questions about TINT? Contact our team for startup support, resources, and platform help. We respond within 1–2 business days."
    ),
    description:
      "Reach out to the TINT team for support with startup resources, projects, equity, or platform questions.",
    twitterDescription: "Get help with startup projects, resources, and your TINT account.",
  },
  {
    path: "/privacy-policy",
    title: "Privacy Policy | TINT",
    seoTitle: "Privacy Policy | TINT",
    ogTitle: "Privacy Policy — Data Protection & Your Rights at TINT",
    metaDescription: truncateMetaDescription(
      "Learn how TINT collects, uses, and protects your personal data, privacy rights, and security practices."
    ),
    description: "Learn how TINT collects, uses, and protects your personal information.",
    twitterDescription: "Read the TINT privacy policy and understand your data rights.",
  },
  {
    path: "/terms-and-conditions",
    title: "Terms and Conditions | TINT",
    seoTitle: "Terms and Conditions | TINT",
    ogTitle: "Terms & Conditions — TINT Startup Platform User Agreement",
    metaDescription: truncateMetaDescription(
      "Read TINT terms of use: user responsibilities, intellectual property, service policies, and platform agreements."
    ),
    description: "Read the terms and conditions for using TINT startup management services.",
    twitterDescription: "Review the terms that govern your use of the TINT platform.",
  },
  {
    path: "/permission-denied",
    title: "Access Denied | TINT",
    seoTitle: "Access Denied | TINT Platform",
    metaDescription: DEFAULT_META_DESCRIPTION,
    description: DEFAULT_OG_DESCRIPTION,
  },
];

export function toAbsoluteUrl(url) {
  if (!url) return DEFAULT_OG_IMAGE;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${SITE_URL}${url}`;
  return `${SITE_URL}/${url}`;
}

export function getCanonicalUrl(pathname) {
  const normalized = pathname || "/";
  if (normalized === "/") return `${SITE_URL}/`;

  const withLeadingSlash = normalized.startsWith("/") ? normalized : `/${normalized}`;
  const withoutTrailingSlash =
    withLeadingSlash.length > 1 && withLeadingSlash.endsWith("/")
      ? withLeadingSlash.slice(0, -1)
      : withLeadingSlash;

  return `${SITE_URL}${withoutTrailingSlash}`;
}

export function getSeoForPath(pathname) {
  for (const route of ROUTE_SEO) {
    if (matchPath({ path: route.path, end: true }, pathname)) {
      const metaDescription = truncateMetaDescription(
        route.metaDescription || route.description || DEFAULT_META_DESCRIPTION
      );
      const rawTitle = route.seoTitle || route.title || DEFAULT_TITLE;
      const seoTitle =
        route.path === "/"
          ? rawTitle
          : ensureMinSeoTitle(rawTitle);

      return {
        title: route.title,
        seoTitle,
        metaDescription,
        ogTitle: route.ogTitle || (route.path === "/" ? rawTitle : seoTitle),
        description: route.description || DEFAULT_OG_DESCRIPTION,
        twitterDescription:
          route.twitterDescription ||
          route.description ||
          DEFAULT_TWITTER_DESCRIPTION,
        path: route.path,
      };
    }
  }

  const seoTitle = ensureMinSeoTitle(DEFAULT_TITLE);

  return {
    title: DEFAULT_TITLE,
    seoTitle,
    metaDescription: DEFAULT_META_DESCRIPTION,
    ogTitle: DEFAULT_OG_TITLE,
    description: DEFAULT_OG_DESCRIPTION,
    twitterDescription: DEFAULT_TWITTER_DESCRIPTION,
    path: null,
  };
}

export function getSeoTitleForPath(pathname) {
  return getSeoForPath(pathname).seoTitle;
}
